/**
 * #5068 — a drawing made by a program (spec: _docs/Implementation/FunctionalSpecs/5068-drawing-program-renderer.md).
 *
 * The scene carries part SPECS (a kit item's name and parameters, a placement, a colour role and the beat that draws
 * it), declared joints and the beats' sentences. It never carries geometry: the server and every renderer expand the
 * same specs with the shared kit, so the room, the TV and the server agree. All of it is data; nothing here runs.
 */
import { z } from 'zod';

/** The drawing's coordinate space: a 960 × 540 sheet (the concept canvas), y down. */
export const DRAWING_WIDTH = 960;
export const DRAWING_HEIGHT = 540;
export const DRAWING_MAX_PARTS = 40;
export const DRAWING_MAX_BEATS = 12;
export const DRAWING_MAX_JOINTS = 16;

const id = z.string().regex(/^[a-zA-Z0-9_-]{1,40}$/);
const x = z.number().finite().min(-DRAWING_WIDTH).max(DRAWING_WIDTH * 2);
const y = z.number().finite().min(-DRAWING_HEIGHT).max(DRAWING_HEIGHT * 2);
export const DrawingPointSchema = z.tuple([x, y]);

/** What a colour means; at most five roles carry colour in one drawing (spec: "colour carries meaning only"). */
export const DRAWING_COLOR_ROLES = ['ink', 'flow', 'heat', 'power', 'life', 'highlight'] as const;
export type DrawingColorRole = typeof DRAWING_COLOR_ROLES[number];

export const DrawingPartSchema = z.object({
  id,
  /** A kit item's name (sketchKit.ts). Unknown names are rejected by the kit, not here. */
  kit: z.string().regex(/^[a-zA-Z][a-zA-Z0-9]{0,30}$/),
  /** Kit parameters; each kit validates and clamps its own. Finite only: Infinity would become null on the wire. */
  params: z.record(z.string().max(30), z.union([z.number().finite(), z.string().max(60), z.boolean(), DrawingPointSchema, z.array(DrawingPointSchema).max(64)])).default({}),
  x: x.default(DRAWING_WIDTH / 2),
  y: y.default(DRAWING_HEIGHT / 2),
  /** Width of the part's unit box, in sheet units. */
  size: z.number().min(4).max(DRAWING_WIDTH).default(120),
  /** Degrees, clockwise in the sheet (and about the viewing axis in 3D): the plane joints move in. */
  rotate: z.number().min(-720).max(720).default(0),
  /** 3D only: depth toward the viewer (sheet units), and turns about the sheet's x axis and vertical axis, in degrees. */
  z: z.number().finite().min(-DRAWING_WIDTH).max(DRAWING_WIDTH).default(0),
  tilt: z.number().min(-360).max(360).default(0),
  yaw: z.number().min(-360).max(360).default(0),
  label: z.string().max(60).default(''),
  explanation: z.string().max(240).default(''),
  color: z.enum(DRAWING_COLOR_ROLES).default('ink'),
  beat: id,
}).strict();
export type DrawingPart = z.infer<typeof DrawingPartSchema>;

/**
 * Motion declared, never computed by model code (D4). One master clock turns `cycles` times per motion period; each
 * joint follows it at `ratio`.
 */
export const DrawingJointSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('rotate'), part: id, ratio: z.number().min(-8).max(8).default(1), phase: z.number().min(0).max(1).default(0) }).strict(),
  z.object({ kind: z.literal('slide'), part: id, dx: z.number().min(-400).max(400), dy: z.number().min(-400).max(400),
    ratio: z.number().min(-8).max(8).default(1), phase: z.number().min(0).max(1).default(0) }).strict(),
  /**
   * A crank turning drives a slider (piston) along `axis` degrees (the direction from crank to slider); an optional link
   * (rod) joins pin and slider. `radius` and `rodLength` default to the drawing's own geometry (the crank's pin, and the
   * pin-to-slider distance as placed), and the crank starts with its pin pointing at the slider.
   */
  z.object({ kind: z.literal('crankSlider'), crank: id, slider: id, link: id.optional(), radius: z.number().min(2).max(300).optional(),
    rodLength: z.number().min(4).max(800).optional(), axis: z.number().min(-360).max(360).default(-90), ratio: z.number().min(-8).max(8).default(1),
    phase: z.number().min(0).max(1).default(0) }).strict(),
  /**
   * A cam turning lifts a follower (valve) along `axis` degrees for `width` of each turn, centred on `center` (the
   * fraction of a turn at which the lift peaks). Without `center`, the lift peaks when the cam's lobe points along `axis`.
   */
  z.object({ kind: z.literal('cam'), cam: id, follower: id, lift: z.number().min(0).max(200), axis: z.number().min(-360).max(360).default(90),
    center: z.number().min(0).max(1).optional(), width: z.number().min(.05).max(1).default(.3),
    ratio: z.number().min(-8).max(8).default(.5), phase: z.number().min(0).max(1).default(0) }).strict(),
  /** A driven gear turns `ratio` times its driver's angle, the opposite way (meshed gears); a negative ratio turns it the same way (a belt). */
  z.object({ kind: z.literal('gear'), driver: id, driven: id, ratio: z.number().min(-8).max(8) }).strict(),
  /** One end of a spring, link, wire or arrow follows another part's movement. */
  z.object({ kind: z.literal('attach'), part: id, end: z.enum(['from', 'to']), follow: id }).strict(),
]);
export type DrawingJoint = z.infer<typeof DrawingJointSchema>;

export const DrawingBeatSchema = z.object({ id, sentence: z.string().min(1).max(240) }).strict();
export type DrawingBeat = z.infer<typeof DrawingBeatSchema>;

export const ContextualVisualDrawingSchema = z.object({
  title: z.string().min(1).max(100),
  subtitle: z.string().max(80).default(''),
  takeaway: z.string().max(80).default(''),
  beats: z.array(DrawingBeatSchema).min(1).max(DRAWING_MAX_BEATS),
  parts: z.array(DrawingPartSchema).min(1).max(DRAWING_MAX_PARTS),
  joints: z.array(DrawingJointSchema).max(DRAWING_MAX_JOINTS).default([]),
  /** Seconds for one turn of the master clock. */
  period: z.number().min(1).max(30).default(4),
  /** A flat whiteboard (the sketch kit) or a 3D model you can turn (the parts kit). */
  view: z.enum(['2d', '3d']).default('2d'),
}).strict().superRefine((drawing, ctx) => {
  const parts = new Set<string>(), beats = new Set(drawing.beats.map(beat => beat.id));
  if (beats.size !== drawing.beats.length) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['beats'], message: 'Beat ids must be unique' });
  for (const [index, part] of drawing.parts.entries()) {
    if (parts.has(part.id)) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['parts', index, 'id'], message: 'Part ids must be unique' });
    if (!beats.has(part.beat)) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['parts', index, 'beat'], message: 'Part beat must exist' });
    parts.add(part.id);
  }
  for (const [index, joint] of drawing.joints.entries()) {
    const refs = joint.kind === 'rotate' || joint.kind === 'slide' ? [joint.part] : joint.kind === 'crankSlider' ? [joint.crank, joint.slider, ...(joint.link ? [joint.link] : [])]
      : joint.kind === 'cam' ? [joint.cam, joint.follower] : joint.kind === 'gear' ? [joint.driver, joint.driven] : [joint.part, joint.follow];
    if (refs.some(ref => !parts.has(ref))) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['joints', index], message: 'Joint parts must exist' });
  }
});
export type ContextualVisualDrawing = z.infer<typeof ContextualVisualDrawingSchema>;
